import {action, observable, runInAction, makeObservable} from "mobx";
import {
    HistoryController_GetGameSessionHistoryRequest,
    HistoryController_GetHistoryRequest,
} from "../../../api/api.service";
import {IServiceFactoryExtended} from "../../../service-factory-extended.interface";
import {IHistoryPaginatorViewModel} from "./history-paginator-view-model.interface";
import {IHistoryPageInfo, IHistoryServerResponseBase} from "./history-server-response-base.interface";
import {NextHistoryPageRequest} from "../../gaming-history.service.interfce";
import {GameInfo} from "../../../games/game-info";
import {IHistoryRecordViewModel} from "../record/history-record-view-model.interface";
import {GamesMap} from "../../../games/games.service.interface";
import {IRegulatedCompanyModel} from "../../../companies/regulated-company-model.interface";
import {Nullable} from "../../../../../common/types/nullable-types";

export abstract class HistoryPaginatorBaseModel<THistoryRecordData,
    TServerResponse extends IHistoryServerResponseBase<THistoryRecordData>,
    TRecordModel extends IHistoryRecordViewModel> implements IHistoryPaginatorViewModel {
    constructor(protected readonly services: IServiceFactoryExtended, private readonly pageSize: number) {
        makeObservable(this);
        this._migratedHistoryPaginator = this._createMigratedHistoryPaginator();
    }

    protected abstract _getNextPage(request: NextHistoryPageRequest): Promise<TServerResponse>;
    protected abstract _getGameTypeFromRecord(recordData: THistoryRecordData): number;
    protected abstract _getRoundIdFromRecord(recordData: THistoryRecordData): string;
    protected abstract _createRecordModel(gameInfo: GameInfo, company: IRegulatedCompanyModel, recordData: THistoryRecordData): TRecordModel;
    protected abstract _createMigratedHistoryPaginator(): IHistoryPaginatorViewModel;

    private readonly _migratedHistoryPaginator: Nullable<IHistoryPaginatorViewModel>;

    get daysIntervals(): number[] {
        return [30, 60, 90];
    }

    private _numberOfHistoryDays: number = 30;
    @action.bound
    setNumberOfHistoryDays(numberOfDays: number): void {
        if (numberOfDays === this._numberOfHistoryDays) {
            return;
        }
        this._migratedHistoryPaginator.setNumberOfHistoryDays(numberOfDays)
        this._numberOfHistoryDays = numberOfDays;
        this._resetPaging();
        this.loadNextPage();
    }

    @action.bound
    private _resetPaging(): void {
        this._records = [];
        this._pages = null;
        this._nextPageIndex = 0;
    }

    @observable.ref
    private _pages: IHistoryPageInfo[] | null = null;


    @observable.ref
    private _nextPageIndex = 0;


    get hasMoreSelfRecords(): boolean {
        if (!this._pages) {
            return true;
        }
        return this._nextPageIndex < this._pages.length;
    }

    get hasMoreRecords(): boolean {
        return this.hasMoreSelfRecords || this._migratedHistoryPaginator.hasMoreRecords
    }

    @observable.ref
    private _isLoading: boolean = false;
    get isLoading(): boolean {
        return this._isLoading || this._migratedHistoryPaginator.isLoading;
    }

    @action.bound
    private _setIsLoading(value: boolean): void {
        this._isLoading = value;
    }

    @observable.ref
    _records: IHistoryRecordViewModel[] = [];
    get records(): IHistoryRecordViewModel[] {
        return [
            ...this._records,
            ...this._migratedHistoryPaginator.records
        ]
    }

    private _createNextPageRequest(): HistoryController_GetHistoryRequest & HistoryController_GetGameSessionHistoryRequest {
        if (this._nextPageIndex === 0) {
            return {
                fromLastDays: this._numberOfHistoryDays,
                pageSize: this.pageSize,
                pageNumber: 1
            };
        }

        const nextPage = this._pages[this._nextPageIndex];
        return {
            fromLastDays: this._numberOfHistoryDays,
            pageNumber: this._nextPageIndex + 1,
            pageSize: this.pageSize,
            requestKey: nextPage.RequestKey,
            minDate: nextPage.MinDate,
            maxDate: nextPage.MaxDate
        };


    }



    private async _getGamesInfo(serverResponse: TServerResponse): Promise<GamesMap> {
        const result: GamesMap = {};
        const notInLayoutGames: number[] = [];

        serverResponse.GameHistoryData.forEach(record => {
            const gameType = this._getGameTypeFromRecord(record);
            const gameInfo = this.services.games.gamesMap[gameType];
            if (gameInfo) {
                result[gameInfo.gameType] = gameInfo;
            } else {
                notInLayoutGames.push(gameType);
            }
        });

        if (notInLayoutGames.length > 0) {
            const gamesMap = await this.services.games.getGamesInfoForHistory(notInLayoutGames);
            Object.keys(gamesMap).forEach(gameType => {
                result[gameType] = gamesMap[gameType];
            });
        }

        return result;
    }



    async loadNextPage(): Promise<void> {

        if (this.isLoading) {
            return;
        }

        /*
        if (!this.hasMoreSelfRecords) {
            if (this._migrationPaginator.hasMoreRecords) {
                await this._migrationPaginator.loadNextPage();
                return;
            }
            return;
        }
        */

        this._setIsLoading(true);

        try {
            const request = this._createNextPageRequest()
            const serverResponse = await this._getNextPage(request);
            const gamesMap = await this._getGamesInfo(serverResponse);

            const newRecords: TRecordModel[] = [];

            for (let recordData of serverResponse.GameHistoryData) {
                const gameType = this._getGameTypeFromRecord(recordData);
                const gameInfo = gamesMap[gameType];
                if (gameInfo) {
                    const company = await (await this.services.companies).getRegulatedCompany(gameInfo.companyName);
                    newRecords.push(this._createRecordModel(gameInfo, company, recordData));
                } else {
                    this.services.logger.error(`Could not get game info for game type ${this._getRoundIdFromRecord(recordData)} in history!`);
                }
            }


            runInAction(() => {
                if (this._nextPageIndex === 0) {
                    this._pages = serverResponse.PagingData;
                }
                this._records = [
                    ...this.records,
                    ...newRecords
                ];

                this._nextPageIndex++;
            });

            if(!this.hasMoreSelfRecords) {
                await this._migratedHistoryPaginator.loadNextPage();
            }

        } finally {
            this._setIsLoading(false);
        }
    }
}